import { Box, Chip, MenuItem, Select } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import { FormattedMessage as T } from "react-intl";
import { useQuery } from "react-query";
import useGlobalContext from "hooks/globalData";
import {
  ListRequestOfBaseFilter,
  ListResponseOfRole,
} from "services/KnowfullyAdminClient";

const useStyles = (_: Theme) => ({
  chips: {
    display: "flex",
    flexWrap: "wrap",
  },
  chip: {
    margin: "2px",
  },
});

const menuProps = {
  PaperProps: {
    style: {
      minWidth: 200,
    },
  },
};

type RoleFieldProps = {
  value?: string[];
  onChange: (v?: string[]) => void;
  disabled?: boolean;
};

const RoleField = ({
  onChange,
  value,
  disabled = false,
}: RoleFieldProps) => {
  const { roleService } = useGlobalContext();
  const styles = useStyles(useTheme());

  const fetchedRoles = useQuery<ListResponseOfRole>(
    ["roles"],
    () =>
      roleService.getRoles(
        ListRequestOfBaseFilter.fromJS({
          pageSize: -1,
          sortField: "name",
        })
      ),
    {
      cacheTime: 60 * 60 * 1000,
      staleTime: 60 * 60 * 1000,
    }
  );

  const handleChangeMultiple = (event: any) => {
    onChange(event.target.value as string[]);
  };

  const items = fetchedRoles.isLoading
    ? [
        <MenuItem key="loading" value="" disabled>
          <T id="loading" />
        </MenuItem>,
      ]
    : (fetchedRoles.data?.data ?? []).map((role) => (
        // A held role the caller may not assign stays selected but cannot be toggled, so it is always resubmitted.
        <MenuItem
          key={role.name}
          value={role.name}
          disabled={role.canAssign === false}
        >
          {role.name}
        </MenuItem>
      ));

  const shouldDisableRoles =
    !!disabled ||
    !!fetchedRoles.error ||
    (!fetchedRoles.isLoading && fetchedRoles.data?.data!.length === 0);

  return (
    <Select
      MenuProps={menuProps}
      multiple
      displayEmpty
      onChange={handleChangeMultiple}
      value={Array.isArray(value) ? value : []}
      fullWidth
      variant="standard"
      disabled={shouldDisableRoles}
      renderValue={(selected: any) => {
        selected = Array.isArray(selected) ? selected : [];
        return (
          <Box sx={styles.chips}>
            {selected.map((value: any) => (
              <Chip
                key={value}
                label={
                  (fetchedRoles.data?.data ?? []).find(
                    (role) => role.name === value
                  )?.name ?? "?"
                }
                sx={styles.chip}
              />
            ))}
          </Box>
        );
      }}
    >
      {items}
    </Select>
  );
};

export default RoleField;
